import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CheckCircle2, AlertCircle, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MIN_LEN = 10;

export default function B2cCompleteInvitePage() {
  const [params]      = useSearchParams();
  const navigate      = useNavigate();
  const [token]       = useState(params.get("token") || "");
  const [password, setPassword]       = useState("");
  const [confirm, setConfirm]         = useState("");
  const [loading, setLoading]         = useState(false);
  const [done, setDone]               = useState(false);
  const [error, setError]             = useState("");

  useEffect(() => {
    if (!token) setError("El enlace no contiene un token válido.");
  }, [token]);

  const validate = () => {
    if (!token) return "Enlace inválido: falta token.";
    if (password.length < MIN_LEN)
      return `La contraseña debe tener al menos ${MIN_LEN} caracteres.`;
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password))
      return "La contraseña debe incluir letras y números.";
    if (password !== confirm)
      return "Las contraseñas no coinciden.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError(""); setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/b2c/admins/complete-invite`,
                                    { token, password });
      if (r.data?.ok) {
        setDone(true);
        toast.success("Contraseña configurada. Redirigiendo…");
        setTimeout(() => navigate("/login", { replace: true }), 1600);
      } else {
        setError("No se pudo completar la invitación.");
      }
    } catch (e) {
      const d = e.response?.data?.detail || "Error al completar invitación.";
      const msg = {
        invalid_token:        "El token no existe o es inválido.",
        wrong_purpose:        "Este enlace no corresponde a una invitación B2C admin.",
        token_already_used:   "Este enlace ya fue utilizado.",
        token_revoked:        "Este enlace fue revocado. Solicita uno nuevo al super_admin.",
        token_expired:        "El enlace expiró. Solicita un nuevo enlace al super_admin.",
        token_invalid_expiry: "El enlace tiene un formato inválido de expiración.",
        user_not_found:       "La cuenta asociada no existe.",
        user_not_b2c_admin:   "La cuenta no es un administrador B2C.",
        user_outside_b2c_scope: "La cuenta no pertenece al tenant B2C.",
      }[d] || d;
      setError(msg);
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="max-w-md w-full" data-testid="invite-success-card">
        <CardContent className="pt-6 flex flex-col items-center gap-3">
          <CheckCircle2 className="h-14 w-14 text-emerald-500"/>
          <h2 className="text-lg font-semibold text-slate-900">
            ¡Contraseña configurada!
          </h2>
          <p className="text-sm text-slate-600 text-center">
            Serás redirigido al inicio de sesión en unos segundos.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Toaster richColors position="top-right"/>
      <Card className="max-w-md w-full" data-testid="invite-complete-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-slate-500"/>
            Activar cuenta B2C Admin
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            BUDDY AVANZA · Configuración inicial de contraseña
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3" data-testid="invite-form">
            {error && (
              <div className="flex gap-2 items-start bg-rose-50 border border-rose-200
                              text-rose-700 text-xs p-2 rounded"
                    data-testid="invite-error">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5"/>
                <span>{error}</span>
              </div>
            )}
            <div>
              <label className="text-xs text-slate-600">Nueva contraseña</label>
              <Input type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder={`Mínimo ${MIN_LEN} caracteres`}
                      autoComplete="new-password"
                      data-testid="input-password"
                      disabled={loading}
                      required minLength={MIN_LEN}/>
            </div>
            <div>
              <label className="text-xs text-slate-600">Confirmar contraseña</label>
              <Input type="password"
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      autoComplete="new-password"
                      data-testid="input-confirm"
                      disabled={loading}
                      required minLength={MIN_LEN}/>
            </div>
            <p className="text-[10px] text-slate-400 leading-snug">
              La contraseña debe incluir al menos {MIN_LEN} caracteres, con
              letras y números. Este enlace es de un solo uso.
            </p>
            <Button type="submit"
                     disabled={loading || !token}
                     className="w-full"
                     data-testid="submit-invite">
              {loading ? "Guardando..." : "Activar cuenta"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
